import {useLayoutEffect} from "react";
import {Navigate} from "react-router-dom";
import {useCabinetData} from "@/hooks/useCabinetData";
import {formatDate} from "@/lib/cabinet";

const DATE_FORMAT: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
};

export function CabinetPauseConfirmedPage() {
    const {subscription, isLoading, isError, refetch} = useCabinetData();

    useLayoutEffect(() => {
        window.scrollTo({top: 0, left: 0, behavior: "auto"});
    }, []);

    if (isLoading) {
        return (
            <div className="cabinet-state">
                <h2>Проверяем статус подписки</h2>
                <p>Подтверждаем, что пауза включена.</p>
            </div>
        );
    }

    if (isError) {
        return (
            <div className="cabinet-state">
                <h2>Не удалось загрузить подтверждение</h2>
                <button type="button" className="btn btn-primary" onClick={() => void refetch()}>
                    Повторить
                </button>
            </div>
        );
    }

    if (!subscription) {
        return <Navigate to="/subscribe/" replace/>;
    }

    if (subscription.status !== "PAUSED") {
        return <Navigate to="/cabinet" replace/>;
    }

    const pauseUntil = formatDate(subscription.pause_until, DATE_FORMAT);
    const nextChargeAt = formatDate(subscription.next_charge_at, DATE_FORMAT);

    return (
        <section className="cabinet-cancel-complete-flow" aria-labelledby="pause-complete-title">
            <article className="cabinet-panel cabinet-cancel-complete-card cabinet-pause-complete-card">
                <div className="cabinet-cancel-complete-visual" aria-hidden="true">
                    <span className="cabinet-cancel-complete-orbit cabinet-cancel-complete-orbit-one"/>
                    <span className="cabinet-cancel-complete-orbit cabinet-cancel-complete-orbit-two"/>
                    <span className="cabinet-cancel-complete-mark cabinet-pause-complete-mark">
                        <svg viewBox="0 0 48 48">
                            <path d="M18 14v20M30 14v20"/>
                        </svg>
                    </span>
                </div>

                <div className="cabinet-cancel-complete-copy cabinet-pause-complete-copy">
                    <span className="cabinet-cancel-eyebrow">Пауза включена</span>
                    <h2 id="pause-complete-title">Подписка приостановлена</h2>
                    <p>
                        Доступ к Лидхолду приостановлен{pauseUntil ? (
                            <> до <time dateTime={subscription.pause_until ?? undefined}>{pauseUntil}</time></>
                        ) : " на 30 дней"}. Во время паузы списаний не будет,
                        а подписка возобновится автоматически.
                    </p>
                </div>

                <div className="cabinet-cancel-complete-status cabinet-pause-complete-status" aria-label="Статус паузы">
                    <div>
                        <span>Возобновление</span>
                        <strong className="is-paused">
                            <i aria-hidden="true"/>
                            {pauseUntil ? (
                                <time dateTime={subscription.pause_until ?? undefined}>{pauseUntil}</time>
                            ) : "Через 30 дней"}
                        </strong>
                        <small>Доступ вернётся автоматически.</small>
                    </div>
                    <div>
                        <span>Следующее списание</span>
                        <strong>
                            {nextChargeAt ? (
                                <time dateTime={subscription.next_charge_at ?? undefined}>{nextChargeAt}</time>
                            ) : "После возобновления"}
                        </strong>
                        <small>Дата оплаты перенесена вместе с подпиской.</small>
                    </div>
                </div>

                <div className="cabinet-cancel-complete-note cabinet-pause-complete-note">
                    <span aria-hidden="true">
                        <svg viewBox="0 0 24 24">
                            <path d="M12 3 5 6v5c0 4.6 2.8 8.2 7 10 4.2-1.8 7-5.4 7-10V6Z"/>
                            <path d="m9 12 2 2 4-5"/>
                        </svg>
                    </span>
                    <div>
                        <strong>Всё останется на месте</strong>
                        <p>Настройки, кампании, диалоги и рабочие материалы сохранятся в аккаунте.</p>
                    </div>
                </div>

                <a
                    className="btn btn-primary cabinet-cancel-complete-action"
                    href="/"
                >
                    Вернуться на главную
                </a>
            </article>
        </section>
    );
}
